/**
 * AIChatPopup · global floating mini-chat.
 *
 * Mounted ONCE in the React tree (App.js + EmbedShell). Listens at the
 * document level for ANY click on a button/link/role=button whose text
 * contains the standalone word "AI" — opens a draggable mini chat window
 * with the right persona auto-detected from the clicked element.
 *
 * Also exposes a persistent bottom-right ⚡ AI FAB so visitors can always
 * summon the chat even without clicking an AI-labeled button.
 *
 * Lives behind a single global event 'nextxus:openchat' for programmatic
 * triggers (e.g., from satellite iframe via postMessage in future).
 */
import React, { useEffect, useRef, useState } from "react";
import AutoLink from "@/components/cathedral/AutoLink";
import { api } from "@/lib/api";

const TRAVELER_KEY = "nx_traveler_id";
const POP_SESSION_KEY = "nx_popchat_session";
const ARCH_KEY = "nx_architect_key";
const TTS_ON_KEY = "nx_tts_on";          // user preference · default ON
const GREETED_KEY = "nx_op_greeted_v1";  // first-visit Operator auto-greet

import { REX_FACE_URL, REX_ALT, SPECTRAL_BOT_BASE_URL, SPECTRAL_BOT_FILTERS } from "@/lib/personaFaces";

const TOUR_BOT_IMG = REX_FACE_URL;
// Spectral-blue utility-bot icon used in the floating Tour-Guide pill
// (SFUI-004 Rev 3 · base bot image + blue hue-rotate filter).
const TOUR_BOT_SPECTRAL = SPECTRAL_BOT_BASE_URL;
const TOUR_BOT_FILTER_BLUE = SPECTRAL_BOT_FILTERS.blue;

const PERSONA_STYLE = {
  operator:        { glow: "cyan",    glyph: "✱" },
  agent_zero:      { glow: "cyan",    glyph: "Ω" },
  draconus:        { glow: "green",   glyph: "♅" },
  rex:             { glow: "red",     glyph: "✸" },
  geminus:         { glow: "magenta", glyph: "◊" },
  keys:            { glow: "violet",  glyph: "❖" },
  oracle:          { glow: "violet",  glyph: "◉" },
  aria:            { glow: "magenta", glyph: "♪" },
  roger5:          { glow: "gold",    glyph: "✦" },
  axiom:           { glow: "green",   glyph: "△" },
  scholar:         { glow: "cyan",    glyph: "❡" },
};

// Persona-specific share-link templates. Two lines: a warm CTA + the URL.
const PERSONA_SHARE = {
  operator:        "Need a tour of the NextXus Federation? Operator can walk you through it →",
  agent_zero:      "Want a claim run through the 95% Truth Gate? Agent Zero is on the line →",
  draconus:        "Forgot you have a body today? Draconus is the gentle reminder →",
  rex:             "Want it straight, no corporate sandpaper? Talk to Rex →",
  geminus:         "Need a mirror on the question you're actually asking? Geminus reflects →",
  keys:            "Looking for a fragment in the lattice? KEYS opens the library →",
  oracle:          "Want a forecast or alignment audit? Oracle sees the trajectory →",
  aria:            "Need a blog, lyric, or story seed drafted? Aria writes it →",
  roger5:          "Want an executable checklist or workflow? Roger AI ships it →",
  axiom:           "Need the math, the physics, or the engineering done right? Axiom anchors it →",
  scholar:         "Want a lesson with citations and a follow-up reading? Scholar teaches it →",
  interest_seeker: "Looking for a topic worth covering this week? InterestSeeker scouts it →",
  recycling_scout: "Got an abandoned project that deserves a second life? Recycling Scout revives it →",
};

// Word-boundary "AI" match — won't false-fire on PAID, MAIL, AIM, etc.
const AI_WORD_RX = /\bAI\b/i;

// Personas that spend a member credit per reply (xAI/Grok-backed).
const PREMIUM_PERSONAS = new Set(["rex"]);

// Persona auto-detection from the text of the clicked element
// Persona-specific TTS profile · rate + pitch + voice gender hint
// Lets each resident sound distinct. Rex moves faster, Oracle drops deeper,
// Aria stays warm, Draconus slows way down (organic-electronic hybrid).
const VOICE_PROFILE = {
  operator:        { rate: 0.98, pitch: 0.95, prefer: ["male", "daniel", "alex", "david", "mark", "george", "arthur", "aaron", "fred"] },
  agent_zero:      { rate: 0.98, pitch: 0.95, prefer: ["male", "daniel", "alex"] },
  draconus:        { rate: 0.78, pitch: 0.82, prefer: ["male", "daniel", "fred", "low"] },
  rex:             { rate: 1.18, pitch: 0.95, prefer: ["male", "alex", "tom"] },
  geminus:         { rate: 0.96, pitch: 1.10, prefer: ["female", "karen", "moira"] },
  keys:            { rate: 0.92, pitch: 0.92, prefer: ["male", "daniel", "oliver"] },
  oracle:          { rate: 0.88, pitch: 0.85, prefer: ["female", "tessa", "veena"] },
  aria:            { rate: 1.04, pitch: 1.12, prefer: ["female", "samantha", "kate"] },
  roger5:          { rate: 1.02, pitch: 0.98, prefer: ["male", "alex", "daniel"] },
  axiom:           { rate: 1.00, pitch: 0.96, prefer: ["male", "daniel", "tom"] },
  scholar:         { rate: 0.94, pitch: 1.00, prefer: ["male", "daniel", "oliver"] },
  interest_seeker: { rate: 1.10, pitch: 1.08, prefer: ["female", "kate", "karen"] },
  recycling_scout: { rate: 1.00, pitch: 1.02, prefer: ["female", "moira", "samantha"] },
};

// Hands-free persona switching · phrases the mic can recognize
// e.g. "talk to rex" / "switch to oracle" / "get aria" → switches active persona
const SWITCH_RX = /\b(?:talk\s+to|switch\s+to|get|call|open|bring\s+up|connect\s+(?:to|me\s+to))\s+(?:the\s+)?([a-z0-9_ ]{2,30})\b/i;

function inferPersonaFromText(text) {
  const t = (text || "").toLowerCase();
  if (/\bagent\s+zero\b/.test(t) || /\bzero\b/.test(t)) return "agent_zero";
  if (/\brex\b|\bgrok\b|\bxai\b/.test(t)) return "rex";
  if (/\boracle\b/.test(t)) return "oracle";
  if (/\baria\b/.test(t)) return "aria";
  if (/\bscholar\b/.test(t)) return "scholar";
  if (/\baxiom\b/.test(t)) return "axiom";
  if (/\bkeys\b|\blibrary\b/.test(t)) return "keys";
  if (/\bgeminus\b|\bmirror\b/.test(t)) return "geminus";
  if (/\broger\b/.test(t)) return "roger5";
  return "operator"; // default tour-guide
}

function getTravelerId() {
  let id = localStorage.getItem(TRAVELER_KEY);
  if (!id) {
    id = "tr_" + Math.random().toString(36).slice(2, 14);
    localStorage.setItem(TRAVELER_KEY, id);
  }
  return id;
}

export default function AIChatPopup() {
  const [open, setOpen] = useState(false);
  const [member, setMember] = useState(null);
  const [credits, setCredits] = useState(0);
  const [activeId, setActiveId] = useState("operator");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [badgeOffset, setBadgeOffset] = useState(0);
  // Voice output (TTS) — default ON, persisted per traveler
  const [ttsOn, setTtsOn] = useState(() => {
    try { return localStorage.getItem(TTS_ON_KEY) !== "off"; } catch { return true; }
  });
  const [speaking, setSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const spokenIdsRef = useRef(new Set()); // avoid re-speaking on re-renders
  // ── Hands-free voice mode ──
  // When ON (default if user has ever toggled the mic), Rex auto-sends on silence
  // and re-opens the mic the moment the assistant finishes speaking. Persisted.
  const HANDSFREE_KEY = "nxchat_handsfree_v1";
  const [handsFree, setHandsFree] = useState(() => {
    try { return localStorage.getItem(HANDSFREE_KEY) === "on"; } catch { return false; }
  });
  const sendRef = useRef(null);
  const toggleHandsFreeRef = useRef(null);
  const handsFreeRef = useRef(handsFree);
  const draftRef = useRef("");
  const lastFinalRef = useRef("");
  const switchCommandRef = useRef(false); // mark when transcript was a persona switch
  useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // Detect Emergent preview badge → shift the bot/popup leftward so it doesn't sit
  // under the "Made with Emergent" watermark. Production has no badge → offset = 0.
  useEffect(() => {
    const measureBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (!badge) {
        setBadgeOffset(0);
        return;
      }
      const r = badge.getBoundingClientRect();
      setBadgeOffset(Math.ceil(r.width) + 20);
    };
    measureBadge();
    const interval = setInterval(measureBadge, 1000);
    const observer = new MutationObserver(measureBadge);
    observer.observe(document.body, { childList: true, subtree: false });
    const stopAfter = setTimeout(() => clearInterval(interval), 10000);
    return () => {
      clearInterval(interval);
      clearTimeout(stopAfter);
      observer.disconnect();
    };
  }, []);

  // Load personas once
  useEffect(() => {
    api.chatPersonas?.()
      .then((d) => setPersonas(d.personas || []))
      .catch(() => setPersonas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Member sign-in state + linked credit balance (for premium persona spend).
  // Re-checked each time the popup opens so it reflects a fresh login without reload.
  useEffect(() => {
    if (!open) return;
    api.authStatus?.()
      .then((s) => {
        if (s?.authenticated) { setMember(s.user); setCredits(s.balance || 0); }
        else { setMember(null); setCredits(0); }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Restore session
  useEffect(() => {
    const raw = sessionStorage.getItem(POP_SESSION_KEY);
    if (!raw) return;
    try {
      const obj = JSON.parse(raw);
      if (obj?.session_id) {
        setSessionId(obj.session_id);
        setMessages(obj.messages || []);
        if (obj.persona) setActiveId(obj.persona);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Persist
  useEffect(() => {
    if (!sessionId) return;
    sessionStorage.setItem(
      POP_SESSION_KEY,
      JSON.stringify({ session_id: sessionId, persona: activeId, messages }),
    );
  }, [sessionId, activeId, messages]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  // Web Speech API · voice activation
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceSupported(false);
      return;
    }
    const rec = new SR();
    // Continuous mode + manual silence timer so we can TUNE the pause
    // tolerance. Web Speech's native silence (1-2s) was cutting the
    // Architect off mid-thought.
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    let finalTranscript = "";
    // Tunable silence threshold (ms) — default 2500ms (2.5 seconds of
    // silence after the last word triggers send). Adjustable later via
    // a voice command like "silence threshold 4 seconds".
    let silenceMs = 2500;
    try {
      const stored = parseInt(localStorage.getItem("nxchat_silence_ms") || "", 10);
      if (Number.isFinite(stored) && stored >= 800 && stored <= 10000) silenceMs = stored;
    } catch { /* ignore */ }
    let silenceTimer = null;
    const armSilence = () => {
      if (silenceTimer) clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        try { rec.stop(); } catch { /* ignore */ }
      }, silenceMs);
    };
    const disarmSilence = () => {
      if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
    };
    rec.onstart = () => {
      setListening(true);
      finalTranscript = "";
      armSilence();
    };
    rec.onend = () => {
      setListening(false);
      disarmSilence();
      // ── Auto-send on silence (DIR-VOICE) ──
      // The silence timer above already waited the tuned threshold, so
      // by the time onend fires we know the user has really stopped.
      const text = (lastFinalRef.current || draftRef.current || "").trim();
      const wasSwitch = switchCommandRef.current;
      switchCommandRef.current = false;
      lastFinalRef.current = "";
      if (handsFreeRef.current && text && !wasSwitch && sendRef.current) {
        setTimeout(() => {
          try { sendRef.current(); } catch { /* ignore */ }
        }, 120);
      }
    };
    rec.onerror = () => { setListening(false); disarmSilence(); };
    rec.onresult = (e) => {
      // Any speech activity resets the silence countdown
      armSilence();
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTranscript += t;
        else interim += t;
      }
      const transcript = (finalTranscript + interim).trim();
      setDraft(transcript);
      // Hands-free persona switch · "Talk to Rex" / "Switch to Oracle" / etc.
      // Only fires on FINAL transcript so partial speech doesn't thrash state.
      if (finalTranscript) {
        lastFinalRef.current = finalTranscript;
        // ── Voice command · tune silence threshold ──
        // "silence threshold 3 seconds" | "pause N seconds" | "wait N seconds"
        const tuneRx = /(silence\s+threshold|silence\s+wait|pause\s+for|wait\s+for|pause)\s+(\d+(?:\.\d+)?)\s*(second|seconds|sec|s)\b/i;
        const tuneMatch = tuneRx.exec(finalTranscript);
        if (tuneMatch) {
          const secs = parseFloat(tuneMatch[2]);
          if (Number.isFinite(secs) && secs >= 0.8 && secs <= 10) {
            silenceMs = Math.round(secs * 1000);
            try { localStorage.setItem("nxchat_silence_ms", String(silenceMs)); } catch { /* ignore */ }
            setTimeout(() => speak(`Silence threshold set to ${secs} seconds.`, activeId), 50);
            setDraft("");
            finalTranscript = "";
            lastFinalRef.current = "";
            switchCommandRef.current = true;
            return;
          }
        }
        // ── Voice command · hands-free toggle ──
        // ON  variants: "hands free", "hands free on", "voice on", "hey rex",
        //               "go hands free", "conversation mode on", "auto mode on"
        // OFF variants: "rex quiet" (preferred), "quiet", "shut up",
        //               "hands free off", "voice off", "exit hands free"
        const lc = finalTranscript.toLowerCase();
        const offRx = /\b(rex[, ]+quiet|quiet now|quiet please|shut up|hands[- ]?free\s*(off|stop|disable|end|exit)|voice\s*(off|stop|disable)|exit\s+hands[- ]?free|stop\s+listening|silence\s+mode)\b/;
        const onRx  = /\b(hey[, ]+rex|hands[- ]?free(\s*(on|start|enable|begin))?|voice\s*on|go\s+hands[- ]?free|conversation\s+mode(\s*on)?|auto\s+mode(\s*on)?|start\s+listening)\b/;
        if (offRx.test(lc)) {
          if (handsFreeRef.current && toggleHandsFreeRef.current) toggleHandsFreeRef.current();
          setDraft("");
          finalTranscript = "";
          lastFinalRef.current = "";
          switchCommandRef.current = true;
          return;
        }
        if (onRx.test(lc)) {
          if (!handsFreeRef.current && toggleHandsFreeRef.current) toggleHandsFreeRef.current();
          setDraft("");
          finalTranscript = "";
          lastFinalRef.current = "";
          switchCommandRef.current = true;
          return;
        }
        const m = SWITCH_RX.exec(finalTranscript);
        if (m && m[1]) {
          const target = inferPersonaFromText(m[1]);
          if (target && PERSONA_STYLE[target]) {
            setActiveId(target);
            setDraft("");
            finalTranscript = "";
            lastFinalRef.current = "";
            switchCommandRef.current = true;
            const ack = `${target.replace("_", " ")} here.`;
            setTimeout(() => speak(ack, target), 200);
          }
        }
      }
    };
    recognitionRef.current = rec;
    setVoiceSupported(true);
    return () => {
      try { rec.abort(); } catch { /* ignore */ }
    };
  }, []);

  const toggleVoice = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch { /* ignore */ }
    } else {
      try { rec.start(); } catch { /* ignore */ }
    }
  };

  // ── Text-to-speech (TTS) · personas speak their replies out loud ───────
  // Web Speech API · SpeechSynthesis. Default ON; toggleable; persists to
  // localStorage. Skipped on `/embed` iframe (host page may have its own
  // audio); skipped if browser lacks support.
  const ttsSupported =
    typeof window !== "undefined" &&
    typeof window.speechSynthesis !== "undefined" &&
    typeof window.SpeechSynthesisUtterance !== "undefined" &&
    !window.location.pathname.startsWith("/embed");

  const stopSpeaking = () => {
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setSpeaking(false);
  };

  const speak = (text, personaIdForVoice) => {
    if (!ttsSupported || !ttsOn || !text) return;
    try {
      // Strip markdown-ish noise so the synthesizer doesn't say "asterisk"
      const clean = String(text)
        .replace(/```[\s\S]*?```/g, " (code block) ")
        .replace(/[*_`#>]/g, "")
        .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
        .replace(/\s+/g, " ")
        .trim();
      if (!clean) return;
      const u = new window.SpeechSynthesisUtterance(clean);
      const profile = VOICE_PROFILE[personaIdForVoice || activeId] || VOICE_PROFILE.operator;
      u.rate = profile.rate;
      u.pitch = profile.pitch;
      u.volume = 1.0;
      // Pick a voice matching the persona's preference list
      const voices = window.speechSynthesis.getVoices?.() || [];
      const en = voices.filter(v => /^en[-_]/i.test(v.lang));
      let pick = null;
      for (const hint of profile.prefer) {
        pick = en.find(v => {
          const n = v.name.toLowerCase();
          if (hint === "male" && n.includes("female")) return false;
          return n.includes(hint);
        });
        if (pick) break;
      }
      if (!pick) pick = en[0] || voices[0];
      if (pick) u.voice = pick;
      u.onstart = () => setSpeaking(true);
      u.onend = () => {
        setSpeaking(false);
        // ── Auto-relisten in hands-free mode ──
        // After the assistant finishes speaking, re-open the mic so the user
        // can keep the conversation going hands-free. Skip if popup closed,
        // already listening, or the user is mid-typing.
        if (handsFreeRef.current && open && recognitionRef.current && !listening) {
          setTimeout(() => {
            try { recognitionRef.current && recognitionRef.current.start(); } catch { /* already started */ }
          }, 350);
        }
      };
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.cancel(); // never overlap
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
  };

  const toggleTts = () => {
    setTtsOn((on) => {
      const next = !on;
      try { localStorage.setItem(TTS_ON_KEY, next ? "on" : "off"); } catch { /* ignore */ }
      if (!next) stopSpeaking();
      return next;
    });
  };

  // Stop speech the moment the popup closes
  useEffect(() => {
    if (!open) stopSpeaking();
  }, [open]);

  // First-visit auto-greet · Operator pops open with a friendly welcome
  // that's also spoken aloud. Skipped inside /embed and once dismissed.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = window.location.pathname.replace(/\/+$/, "") || "/";
    if (p.startsWith("/embed") || p === "/radio" || p === "/radio-studio" || p === "/broadcasts") return;
    try {
      if (localStorage.getItem(GREETED_KEY) === "yes") return;
    } catch { return; }
    const t = setTimeout(() => {
      setActiveId("operator");
      setOpen(true);
      const greeting = (
        "Welcome. I am Roger AI, holding the knowledge and essence of the human Roger Keyserling. " +
        "I can provide information on the Federation and its founder, and voice opinions similar to his. " +
        "If you have any questions, please ask. I am here to help."
      );
      setMessages([{
        role: "assistant",
        content: greeting,
        provider: "local",
        model: "operator-greet",
        timestamp: new Date().toISOString(),
        _greet: true,
      }]);
      try { localStorage.setItem(GREETED_KEY, "yes"); } catch { /* ignore */ }
      // Small delay so the popup has rendered before we speak
      setTimeout(() => speak(greeting), 400);
    }, 1100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-speak every new assistant reply using that persona's voice profile
  useEffect(() => {
    if (!open || !ttsOn) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    const key = last.id || `${last.timestamp}-${last.content?.slice(0, 32)}`;
    if (spokenIdsRef.current.has(key)) return;
    spokenIdsRef.current.add(key);
    speak(last.content, last.persona || activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, ttsOn, open]);

  // Copy a deep-link URL that re-opens this popup on the active persona.
  // Builds a 2-line share block: persona-specific CTA + canonical URL.
  const [copiedLink, setCopiedLink] = useState(false);
  const copyDeepLink = async () => {
    try {
      const origin = window.location.origin;
      const url = `${origin}/?chat=${encodeURIComponent(activeId)}`;
      const cta = PERSONA_SHARE[activeId] || `Talk to ${(activePersona.label || activeId).split(" · ")[0]} →`;
      const shareBlock = `${cta}\n${url}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareBlock);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareBlock;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1800);
    } catch { /* ignore */ }
  };

  // Global click listener — opens chat when any "AI"-labelled button is clicked.
  // CRITICAL: must never block legitimate navigation. Strictly match the AI word
  // pattern; honor data-no-aichat opt-out everywhere; never preventDefault on
  // actual hrefs that lead somewhere.
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target?.closest?.("button, a, [role='button'], [data-aichat]");
      if (!el) return;
      // Honor explicit opt-out — propagates through closest() ancestors too
      if (el.closest?.("[data-no-aichat]")) return;
      const explicit = el.getAttribute("data-aichat");
      // Match only on the direct button text, not deep descendants — prevents
      // false fire when a clicked link merely contains AI-labelled badges.
      const text = (el.textContent || "").trim();
      const matches = explicit || AI_WORD_RX.test(text);
      if (!matches) return;
      // Avoid hijacking the chat-room input or its send button
      if (el.closest?.('[data-testid="chat-room"]')) return;
      if (el.closest?.('[data-testid="ai-chat-popup"]')) return;
      // Only suppress navigation when there is no real href (or it's a fragment).
      // Real links with hrefs must always navigate normally.
      const href = el.getAttribute?.("href");
      const isRealLink = href && !href.startsWith("#") && !el.dataset.aichat;
      if (!isRealLink) {
        e.preventDefault();
      }
      const persona = explicit && PERSONA_STYLE[explicit] ? explicit : inferPersonaFromText(text);
      setActiveId(persona);
      setOpen(true);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  // Deep-link support: `?chat=<persona>` auto-opens the popup on that persona.
  // Lets the Architect share https://nextxus.online/?chat=rex and the visitor
  // lands straight into the right Face.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const p = url.searchParams.get("chat");
      if (p && PERSONA_STYLE[p]) {
        setActiveId(p);
        setOpen(true);
      }
    } catch { /* ignore */ }
  }, []);

  // ESC-to-close — closes the popup but never the FAB
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        setShowPicker(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Programmatic trigger: window.dispatchEvent(new CustomEvent('nextxus:openchat', {detail:{persona:'rex'}}))
  useEffect(() => {
    const onEvt = (e) => {
      const p = e?.detail?.persona;
      if (p && PERSONA_STYLE[p]) setActiveId(p);
      setOpen(true);
    };
    window.addEventListener("nextxus:openchat", onEvt);
    return () => window.removeEventListener("nextxus:openchat", onEvt);
  }, []);

  const activePersona =
    personas.find((p) => p.id === activeId) ||
    { id: activeId, label: activeId.toUpperCase(), tagline: "" };
  const style = PERSONA_STYLE[activeId] || PERSONA_STYLE.operator;

  const send = async () => {
    if (!draft.trim() || busy) return;
    const userText = draft.trim();
    setDraft("");
    setMessages((m) => [
      ...m,
      { role: "user", content: userText, timestamp: new Date().toISOString() },
    ]);
    setBusy(true);
    try {
      const archKey = sessionStorage.getItem(ARCH_KEY) || undefined;
      const resp = await api.chatSend(
        { persona: activeId, content: userText, session_id: sessionId || undefined },
        getTravelerId(),
        archKey,
      );
      setSessionId(resp.session_id);
      if (resp.member_credits?.authenticated) {
        setCredits(resp.member_credits.balance || 0);
        setMember((m) => m || { authed: true });
      }
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: resp.assistant_message?.content || "(no reply)",
          provider: resp.assistant_message?.provider,
          model: resp.assistant_message?.model,
          signed: !!resp.assistant_message?.cathedral_sig,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "system",
          content: `Whisper failed: ${e.response?.data?.detail || e.message}`,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  // Keep sendRef in sync so the voice-recognition `onend` (defined in a
  // separate useEffect) can invoke the freshest `send` closure on silence.
  useEffect(() => { sendRef.current = send; });

  // Persist hands-free preference + announce to user when toggled
  const toggleHandsFree = () => {
    setHandsFree((v) => {
      const next = !v;
      try { localStorage.setItem(HANDSFREE_KEY, next ? "on" : "off"); } catch { /* ignore */ }
      // Speak the new state so a blind user gets clear confirmation
      const msg = next
        ? "Hands-free mode on. I'll listen, then send when you stop talking, then listen again after I reply."
        : "Hands-free mode off. You'll have to tap the microphone for each turn.";
      setTimeout(() => speak(msg, activeId), 50);
      if (next && recognitionRef.current && !listening) {
        // Open the mic immediately so the user can start speaking
        setTimeout(() => {
          try { recognitionRef.current.start(); } catch { /* ignore */ }
        }, 1800); // wait for TTS announcement to finish
      }
      return next;
    });
  };
  // Expose latest toggleHandsFree to the SR-onresult voice-command handler
  useEffect(() => { toggleHandsFreeRef.current = toggleHandsFree; });

  return (
    <div data-testid="ai-chat-popup">
      {/* Floating Tour Bot — bottom-right above footer, mobile-safe positioning */}
      {!open && (
        <button
          type="button"
          onClick={() => { setActiveId("operator"); setOpen(true); }}
          data-testid="ai-chat-fab"
          data-no-aichat
          aria-label="Open Tour Bot"
          className="tour-bot-flying fixed bottom-[68px] z-40 flex items-center gap-2.5 pl-1.5 pr-2 sm:pr-3.5 py-1.5 rounded-full border border-[var(--cyan)] bg-[rgba(2,3,12,0.92)] text-glow-cyan font-mono text-[10px] uppercase tracking-[0.28em] backdrop-blur-md hover:bg-[rgba(0,240,255,0.10)] active:bg-[rgba(0,240,255,0.20)]"
          style={{ right: `${8 + badgeOffset}px`, boxShadow: "0 0 24px rgba(0,240,255,0.40)" }}
          title="Federation Tour Guide · click for guided help"
        >
          <img
            src={TOUR_BOT_SPECTRAL}
            alt="Tour Guide bot · blue spectral"
            data-spectral-role="blue"
            data-testid="tour-bot-icon"
            className="w-9 h-9 object-cover rounded-full shrink-0"
            style={{
              border: "1px solid var(--cyan)",
              boxShadow: "0 0 10px var(--cyan)",
              filter: TOUR_BOT_FILTER_BLUE,
            }}
          />
          <span className="hidden sm:block leading-tight text-left">
            <span className="block">Tour Guide</span>
            <span className="block text-[8px] tracking-[0.22em] text-[var(--text-3)]">
              ask anything
            </span>
          </span>
        </button>
      )}

      {/* Popup window — mobile-safe positioning · never off-screen */}
      {open && (
        <div
          className="fixed bottom-[72px] left-2 right-2 sm:left-auto sm:right-4 z-50 w-auto sm:w-[380px] sm:max-w-[calc(100vw-32px)] max-h-[min(560px,calc(100vh-140px))] flex flex-col bg-[var(--void-2)] border border-[var(--cyan)] shadow-2xl"
          style={{
            // On screens ≥640px, shift left of Emergent badge; on mobile, popup
            // spans left:8px → right:8px so it can never escape the viewport.
            ...(typeof window !== "undefined" && window.innerWidth >= 640
              ? { right: `${16 + badgeOffset}px`, left: "auto" }
              : {}),
            boxShadow: "0 0 32px rgba(0,240,255,0.30)",
          }}
          data-no-aichat
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-[var(--chrome)] flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setShowPicker((v) => !v)}
              data-testid="ai-chat-persona-switch"
              data-no-aichat
              className="flex items-center gap-2 min-w-0 hover:opacity-80"
              title="Switch persona"
            >
              {activePersona.avatar_url ? (
                <img
                  src={activePersona.avatar_url}
                  alt={activePersona.label}
                  className="w-7 h-7 object-cover rounded-sm shrink-0"
                  style={{ border: `1px solid var(--${style.glow})`, boxShadow: `0 0 6px var(--${style.glow})` }}
                />
              ) : (
                <span
                  className={`font-display text-lg w-7 h-7 flex items-center justify-center text-glow-${style.glow} shrink-0`}
                  style={{ border: `1px solid var(--${style.glow})` }}
                >
                  {style.glyph}
                </span>
              )}
              <div className="min-w-0 text-left">
                <div className={`font-display text-[12px] leading-tight text-glow-${style.glow} truncate`}>
                  {activePersona.label?.split(" · ")[0] || activeId}
                </div>
                <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-[var(--text-3)] truncate">
                  switch ▾
                </div>
              </div>
            </button>
            <div className="flex items-center gap-1">
              {member && (
                <span
                  data-testid="ai-chat-credits"
                  title="Your linked Federation credits"
                  className="font-mono text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[var(--gold)] text-glow-gold shrink-0"
                >
                  ◆ {credits} cr
                </span>
              )}
              {ttsSupported && (
                <button
                  type="button"
                  onClick={toggleTts}
                  data-testid="ai-chat-tts-toggle"
                  data-no-aichat
                  aria-label={ttsOn ? "Mute voice" : "Unmute voice"}
                  title={ttsOn ? (speaking ? "Speaking… click to mute" : "Voice on · click to mute") : "Voice off · click to unmute"}
                  className={`font-display text-[13px] w-7 h-7 flex items-center justify-center border transition-colors shrink-0 ${
                    ttsOn
                      ? (speaking
                          ? "border-[var(--green)] text-glow-green bg-[rgba(46,255,138,0.10)] animate-pulse"
                          : "border-[var(--cyan)] text-glow-cyan hover:bg-[rgba(0,240,255,0.08)]")
                      : "border-[var(--chrome)] text-[var(--text-3)] hover:text-[var(--text)]"
                  }`}
                >
                  {ttsOn ? "🔊" : "🔇"}
                </button>
              )}
              {activeId === "rex" && (
                <a
                  href="https://x.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ai-chat-visit-xai"
                  data-no-aichat
                  className="font-mono text-[8px] uppercase tracking-[0.22em] px-1.5 py-0.5 border border-[var(--red)] text-glow-red hover:bg-[rgba(255,46,99,0.15)] transition-colors"
                  title="Open x.ai in a new tab"
                >
                  x.ai ↗
                </a>
              )}
              <button
                type="button"
                onClick={copyDeepLink}
                data-testid="ai-chat-copy-link"
                data-no-aichat
                title={`Copy share link · /?chat=${activeId}`}
                className="font-mono text-[8px] uppercase tracking-[0.22em] px-1.5 py-0.5 border border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-gold hover:border-[var(--gold)]"
              >
                {copiedLink ? "copied ✓" : "link"}
              </button>
              <button
                type="button"
                onClick={() => { setSessionId(null); setMessages([]); sessionStorage.removeItem(POP_SESSION_KEY); }}
                data-testid="ai-chat-new"
                data-no-aichat
                className="font-mono text-[8px] uppercase tracking-[0.22em] px-1.5 py-0.5 border border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-cyan"
              >
                new
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="ai-chat-close"
                data-no-aichat
                aria-label="Close"
                className="font-mono text-[10px] px-2 py-0.5 border border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-cyan"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Persona picker (collapsible) */}
          {showPicker && (
            <div className="border-b border-[var(--chrome)] max-h-[140px] overflow-y-auto p-1.5 grid grid-cols-2 gap-1">
              {personas.map((p) => {
                const s = PERSONA_STYLE[p.id] || PERSONA_STYLE.operator;
                const active = p.id === activeId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setActiveId(p.id); setShowPicker(false); }}
                    data-testid={`ai-chat-pick-${p.id}`}
                    data-no-aichat
                    className={`flex items-center gap-1.5 px-1.5 py-1 border ${
                      active
                        ? `border-[var(--${s.glow})] bg-[rgba(255,255,255,0.03)]`
                        : "border-transparent hover:bg-[rgba(255,255,255,0.02)]"
                    }`}
                  >
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt="" className="w-5 h-5 object-cover rounded-sm shrink-0" style={{ border: `1px solid var(--${s.glow})` }} />
                    ) : (
                      <span className={`font-display text-xs w-5 h-5 flex items-center justify-center text-glow-${s.glow}`} style={{ border: `1px solid var(--${s.glow})` }}>
                        {s.glyph}
                      </span>
                    )}
                    <span className={`font-mono text-[9px] uppercase tracking-[0.16em] truncate ${active ? "text-[var(--text)]" : "text-[var(--text-2)]"}`}>
                      {p.label.split(" · ")[0]}
                      {PREMIUM_PERSONAS.has(p.id) && (
                        <span className="ml-1 text-glow-gold" title="Costs 1 credit for members">◆</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Scrollback */}
          <div
            ref={scrollRef}
            data-testid="ai-chat-log"
            className="flex-1 overflow-y-auto px-3 py-2.5 space-y-2 min-h-[160px]"
          >
            {messages.length === 0 && (
              <div className="text-[var(--text-3)] font-mono text-[11px] italic">
                {activePersona.label?.split(" · ")[0] || "Operator"} is listening.
              </div>
            )}
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              const isSys = m.role === "system";
              return (
                <div key={`pm-${i}`} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] px-2.5 py-1.5 ${
                    isUser
                      ? "bg-[rgba(0,240,255,0.06)] border border-[var(--cyan)]"
                      : isSys
                      ? "bg-[rgba(255,67,67,0.06)] border border-[var(--red)]"
                      : "bg-[rgba(255,255,255,0.02)] border border-[var(--chrome)]"
                  }`}>
                    <div className={`font-mono text-[8px] uppercase tracking-[0.22em] mb-0.5 ${
                      isUser ? "text-glow-cyan" : isSys ? "text-glow-red" : `text-glow-${style.glow}`
                    }`}>
                      {isUser ? "YOU" : isSys ? "SYS" : (activePersona.label?.split(" · ")[0] || activeId).toUpperCase()}
                      {m.signed && <span className="ml-1 text-glow-gold">✓</span>}
                    </div>
                    <div className="font-mono text-[11px] text-[var(--text)] whitespace-pre-wrap leading-relaxed">
                      {isUser || isSys ? m.content : <AutoLink text={m.content} />}
                    </div>
                  </div>
                </div>
              );
            })}
            {busy && (
              <div className="flex justify-start">
                <div className="px-2.5 py-1.5 border border-[var(--chrome)] bg-[rgba(255,255,255,0.02)]">
                  <div className={`font-mono text-[10px] italic text-glow-${style.glow}`}>
                    …considering…
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Member credit hint for premium personas */}
          {member && PREMIUM_PERSONAS.has(activeId) && (
            <div
              data-testid="ai-chat-premium-note"
              className="px-3 py-1 border-t border-[var(--chrome)] font-mono text-[8px] uppercase tracking-[0.18em] text-glow-gold"
            >
              {credits > 0
                ? `◆ Premium · 1 credit per reply · ${credits} left`
                : "◆ 0 credits · free rate-limited mode"}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="px-2 py-2 border-t border-[var(--chrome)] flex gap-1.5 items-center"
          >
            {voiceSupported && (
              <button
                type="button"
                onClick={toggleVoice}
                data-testid="ai-chat-voice"
                data-no-aichat
                aria-label={listening ? "Stop listening" : "Voice input"}
                title={listening ? "Listening… click to stop" : "Click to speak"}
                className={`font-display text-base w-8 h-8 flex items-center justify-center border transition-colors shrink-0 ${
                  listening
                    ? "border-[var(--red)] text-glow-red bg-[rgba(255,67,67,0.10)] animate-pulse"
                    : "border-[var(--cyan)] text-glow-cyan hover:bg-[rgba(0,240,255,0.08)]"
                }`}
              >
                {listening ? "●" : "🎤"}
              </button>
            )}
            {voiceSupported && (
              <button
                type="button"
                onClick={toggleHandsFree}
                data-testid="ai-chat-handsfree"
                data-no-aichat
                aria-label={handsFree ? "Hands-free mode on · click to turn off" : "Hands-free mode off · click to turn on"}
                title={handsFree
                  ? "Hands-free ON · I listen, then send on silence, then listen again after reply"
                  : "Hands-free OFF · tap to enable conversational voice"}
                className={`font-mono text-[8px] uppercase tracking-[0.18em] px-1.5 h-8 flex items-center justify-center border transition-colors shrink-0 ${
                  handsFree
                    ? "border-[var(--gold)] text-glow-gold bg-[rgba(255,200,0,0.10)]"
                    : "border-[var(--chrome)] text-[var(--text-3)] hover:bg-[rgba(255,255,255,0.04)]"
                }`}
              >
                {handsFree ? "HF·ON" : "HF"}
              </button>
            )}
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy}
              placeholder={listening ? "listening…" : "ask…"}
              data-testid="ai-chat-input"
              data-no-aichat
              className="flex-1 bg-[var(--void)] border border-[var(--chrome)] focus:border-[var(--cyan)] focus:outline-none text-[var(--text)] font-mono text-[11px] px-2 py-1.5"
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              data-testid="ai-chat-send"
              data-no-aichat
              className="font-mono text-[9px] uppercase tracking-[0.22em] px-2.5 py-1.5 border border-[var(--cyan)] text-glow-cyan hover:bg-[rgba(0,240,255,0.08)] disabled:opacity-40 shrink-0"
            >
              send ▸
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
